import { useState, useCallback, useEffect } from "react";
import { normalizeAppPath, ROUTES } from "@/lib/routes";
import { navigateByNavId, navigateHome, GLOBAL_NAV_ITEMS } from "@/lib/navigation";
import { Header } from "@/pages/homepage/components";
import type { HeaderConfig, PageProps } from "@/lib/header-config";
import { Homepage } from "@/pages/homepage";
import { MembersPage } from "@/pages/members";
import { RoadmapPage } from "@/pages/roadmap";
import { NotFoundPage } from "@/pages/not-found";

function ComingSoonPage({ onHeaderConfig, onHeroReady }: PageProps) {
  useEffect(() => {
    onHeaderConfig({ activeSection: "history", pageTitle: "연혁", darkHero: true });
    onHeroReady();
  }, []);

  return (
    <div className="min-h-screen bg-[#0c0c0d] flex flex-col items-center justify-center gap-4 text-center px-6">
      <div className="text-[13px] font-semibold tracking-[0.18em] uppercase text-white/30">
        Coming Soon
      </div>
      <h1 className="text-[clamp(36px,6vw,72px)] font-bold leading-[1.1] tracking-[-0.04em] text-white">
        아직 개발 중이에요
      </h1>
      <p className="text-[clamp(15px,1.8vw,20px)] text-white/45 leading-[1.6] tracking-[-0.02em]">
        조금만 기다려 주세요 :)
      </p>
    </div>
  );
}

function useRoute() {
  const [path, setPath] = useState(normalizeAppPath(window.location.pathname));
  useEffect(() => {
    const handler = () => setPath(normalizeAppPath(window.location.pathname));
    window.addEventListener("popstate", handler);
    return () => window.removeEventListener("popstate", handler);
  }, []);
  return path;
}

export function App() {
  const path = useRoute();
  const [heroReady, setHeroReady] = useState(false);
  const [headerConfig, setHeaderConfig] = useState<HeaderConfig>({
    activeSection: "home",
    darkHero: true,
  });

  const isHome = path === ROUTES.home;
  const isKnownRoute = isHome
    || path === ROUTES.history
    || path === ROUTES.members
    || path === ROUTES.activity
    || path === ROUTES.activityLegacy;
  const onScrollTop = isHome
    ? () => window.scrollTo({ top: 0, behavior: "smooth" })
    : navigateHome;

  const onHeroReady = useCallback(() => setHeroReady(true), []);
  const onHeaderConfig = useCallback((cfg: HeaderConfig) => setHeaderConfig(cfg), []);

  return (
    <>
      <Header
        activeSection={headerConfig.activeSection}
        heroReady={heroReady}
        navItems={GLOBAL_NAV_ITEMS}
        pageTitle={headerConfig.pageTitle}
        darkHero={headerConfig.darkHero ?? true}
        onScrollTop={onScrollTop}
        onNavigate={navigateByNavId}
      />
      {isHome && (
        <Homepage onHeaderConfig={onHeaderConfig} onHeroReady={onHeroReady} />
      )}
      {path === ROUTES.history && (
        <ComingSoonPage onHeaderConfig={onHeaderConfig} onHeroReady={onHeroReady} />
      )}
      {path === ROUTES.members && (
        <MembersPage onHeaderConfig={onHeaderConfig} onHeroReady={onHeroReady} />
      )}
      {(path === ROUTES.activity || path === ROUTES.activityLegacy) && (
        <RoadmapPage onHeaderConfig={onHeaderConfig} onHeroReady={onHeroReady} />
      )}
      {!isKnownRoute && (
        <NotFoundPage onHeaderConfig={onHeaderConfig} onHeroReady={onHeroReady} />
      )}
    </>
  );
}

export default App;
